import { useRef, useMemo, useCallback, useState, useEffect } from 'react'
import { styled, CircularProgress, Tooltip } from '@mui/material'
import { Button } from '../../../../../components/buttons'
import { colorPalette } from '../../../../../style/colorPalette'
import { CloseButton } from '../../commons'
import { useVersionPageEntityMap } from './hooks/useVersionPageEntityMap'
import { PageTypeLabel, PAGE_TYPE_LABELS, PAGE_TYPE_MAPPING } from './constants'
import { PageThumbnailList } from './components/PageThumbnailList'
import { PageStatus } from './components/PageThumbnailList/components/PageThumbnailItem'
import { PageConfigDialog } from './components/PageConfigDialog'
import { AddPageMenu } from './components/AddPageMenu'
import { useVersionConfigDialog } from './hooks/useVersionConfigDialog'
import {
  validatePageEntity,
  PageValidationError,
} from '../../../../../../domain/entity/version-report/VersionReportVersionEntity/validator'
import { VersionReportVersionEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  toEntityForValidation,
  VersionReportVersionPageFormModel,
} from './model/VersionReportVersionPageFormModel'

const PAGE_TYPE_LABEL_VALUES: PageTypeLabel[] = Object.values(PAGE_TYPE_LABELS)

const UNCONFIGURED_ERROR_FIELDS = new Set<string>([
  'targetUuids',
  'tableRows',
  'rootUuid',
])

type VersionConfigDialogProps = {
  projectUuid: string
  open: boolean
  original: VersionReportVersionEntity
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  onPreviewPageChange?: (page: VersionReportVersionPageFormModel | null) => void
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void
}

export const VersionConfigDialog = ({
  projectUuid,
  open,
  original,
  onAfterSubmit,
  onClose,
  onPreviewPageChange,
  onUnsavedChangesChange,
}: VersionConfigDialogProps) => {
  const thumbnailScrollRef = useRef<HTMLDivElement>(null)

  const {
    pageEntityMap,
    currentSelectedPageNumber,
    setCurrentSelectedPageNumber,
    addPageEntity,
    updatePageEntity,
    deletePageEntity,
    reorderPages,
    resetPageEntityMap,
  } = useVersionPageEntityMap(original.pages)

  const {
    loading,
    handleSubmit: submitConfig,
    handleClose,
  } = useVersionConfigDialog({
    original,
    onAfterSubmit,
    onClose,
    resetPageEntityMap,
  })

  const sortedPages = useMemo(
    () =>
      Array.from(pageEntityMap.values()).sort(
        (a, b) => a.pageNumber - b.pageNumber
      ),
    [pageEntityMap]
  )

  const pageValidationMap = useMemo(() => {
    const map = new Map<number, PageValidationError[]>()
    sortedPages.forEach(page => {
      const errors = validatePageEntity(toEntityForValidation(page))
      if (errors.length > 0) {
        map.set(page.pageNumber, errors)
      }
    })
    return map
  }, [sortedPages])

  const pageErrorMap = useMemo(() => {
    const map = new Map<number, boolean>()
    sortedPages.forEach(page => {
      map.set(page.pageNumber, pageValidationMap.has(page.pageNumber))
    })
    return map
  }, [sortedPages, pageValidationMap])

  const pageStatusMap = useMemo(() => {
    const originalByUuid = new Map(
      original.pages.map(p => [p.uuid, p] as const)
    )
    const map = new Map<number, PageStatus>()
    sortedPages.forEach(page => {
      if (page.uuid === null) {
        map.set(page.pageNumber, 'new')
        return
      }
      const orig = originalByUuid.get(page.uuid)
      if (!orig) {
        map.set(page.pageNumber, 'new')
        return
      }
      const isEdited =
        orig.pageTitle !== page.pageTitle ||
        JSON.stringify(orig.config) !== JSON.stringify(page.config)
      map.set(page.pageNumber, isEdited ? 'edited' : 'unchanged')
    })
    return map
  }, [original.pages, sortedPages])

  const hasUnsavedChanges = useMemo(
    () =>
      Array.from(pageStatusMap.values()).some(
        s => s === 'new' || s === 'edited'
      ),
    [pageStatusMap]
  )
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const pageUnconfiguredMap = useMemo(() => {
    const map = new Map<number, boolean>()
    sortedPages.forEach(page => {
      const errors = pageValidationMap.get(page.pageNumber) ?? []
      const isUnconfigured = errors.some(err =>
        UNCONFIGURED_ERROR_FIELDS.has(err.field)
      )
      map.set(page.pageNumber, isUnconfigured)
    })
    return map
  }, [sortedPages, pageValidationMap])

  const hasAnyValidationError = useMemo(
    () => pageValidationMap.size > 0,
    [pageValidationMap]
  )

  const handleSubmit = async () => {
    try {
      await submitConfig(sortedPages)
    } catch {
      // エラー通知は useVersionConfigDialog 内で dispatch 済み。
      // ここで unhandled promise rejection を抑止する。
    }
  }

  const [configDialogPageNumber, setConfigDialogPageNumber] = useState<
    number | null
  >(null)

  const configDialogPage = useMemo(() => {
    if (configDialogPageNumber === null) return null
    return pageEntityMap.get(configDialogPageNumber) ?? null
  }, [pageEntityMap, configDialogPageNumber])

  const handleOpenConfig = useCallback(
    (pageNumber: number) => {
      setCurrentSelectedPageNumber(pageNumber)
      setConfigDialogPageNumber(pageNumber)
    },
    [setCurrentSelectedPageNumber]
  )

  const handleCloseConfig = useCallback(() => {
    setConfigDialogPageNumber(null)
  }, [])

  const handleApplyConfig = useCallback(
    (updated: VersionReportVersionPageFormModel) => {
      updatePageEntity(updated.pageNumber, () => updated)
      setConfigDialogPageNumber(null)
    },
    [updatePageEntity]
  )

  useEffect(() => {
    if (!onPreviewPageChange) return
    // PageConfigDialog open 中は反映抑止 (適用前にプレビュー切替されると UX 上違和感が出るため)
    if (configDialogPageNumber !== null) return
    if (currentSelectedPageNumber === null) {
      onPreviewPageChange(null)
      return
    }
    const page = pageEntityMap.get(currentSelectedPageNumber) ?? null
    onPreviewPageChange(page)
  }, [
    pageEntityMap,
    currentSelectedPageNumber,
    configDialogPageNumber,
    onPreviewPageChange,
  ])

  const handlePageTypeSelect = useCallback(
    (pageTypeLabel: PageTypeLabel) => {
      const pageType = PAGE_TYPE_MAPPING[pageTypeLabel]
      const newPageNumber = addPageEntity(pageType, pageTypeLabel)
      setConfigDialogPageNumber(newPageNumber)
      setTimeout(() => {
        const scrollEl = thumbnailScrollRef.current
        if (!scrollEl) return
        scrollEl.scrollTo({
          top: scrollEl.scrollHeight,
          behavior: 'smooth',
        })
      }, 0)
    },
    [addPageEntity]
  )

  if (!open) return null

  return (
    <>
      <PanelContainer>
        <PanelHeader>
          <HeaderLeft>
            <PanelTitle>ページ構成編集</PanelTitle>
            <PanelSubtitle>
              {sortedPages.length}ページ
              {hasUnsavedChanges && (
                <>
                  {' · '}
                  <UnsavedMark>未保存</UnsavedMark>
                </>
              )}
            </PanelSubtitle>
          </HeaderLeft>
          <HeaderRight>
            <Tooltip
              title={
                hasAnyValidationError
                  ? 'エラーのあるページを修正してください'
                  : ''
              }
              arrow={true}
            >
              <SubmitButtonWrapper>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    loading || sortedPages.length === 0 || hasAnyValidationError
                  }
                  colorPattern="skyBlue"
                  size="s"
                >
                  {loading ? '保存中...' : '完了'}
                </Button>
                {loading && <LoadingSpinner size={16} />}
              </SubmitButtonWrapper>
            </Tooltip>
            <CloseButton onClick={handleClose} />
          </HeaderRight>
        </PanelHeader>
        <PanelBody>
          <ThumbnailScrollArea ref={thumbnailScrollRef}>
            <PageThumbnailList
              pages={sortedPages}
              selectedPageNumber={currentSelectedPageNumber}
              pageErrorMap={pageErrorMap}
              pageUnconfiguredMap={pageUnconfiguredMap}
              pageStatusMap={pageStatusMap}
              onSelectPage={setCurrentSelectedPageNumber}
              onOpenConfigForPage={handleOpenConfig}
              onDeletePage={deletePageEntity}
              onReorderPages={reorderPages}
            />
            <AddPageMenu
              pageTypeLabels={PAGE_TYPE_LABEL_VALUES}
              onPageTypeSelect={handlePageTypeSelect}
              disabled={loading}
            />
          </ThumbnailScrollArea>
        </PanelBody>
      </PanelContainer>
      <PageConfigDialog
        open={configDialogPageNumber !== null}
        page={configDialogPage}
        snapshotUuid={original.snapshotUuid}
        onApply={handleApplyConfig}
        onCancel={handleCloseConfig}
      />
    </>
  )
}

const PanelContainer = styled('div')({
  width: '260px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const PanelHeader = styled('div')({
  padding: '12px 16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
})

const HeaderLeft = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
  flex: 1,
})

const PanelTitle = styled('div')({
  fontSize: '14px',
  fontWeight: 'bold',
  color: colorPalette.monotone[10],
})

const PanelSubtitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '11px',
  color: colorPalette.monotone[5],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const UnsavedMark = styled('span')({
  color: colorPalette.pink[5],
  fontWeight: 'bold',
})

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
})

const SubmitButtonWrapper = styled('span')({
  position: 'relative',
  display: 'inline-flex',
})

const LoadingSpinner = styled(CircularProgress)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-8px',
  marginLeft: '-8px',
  color: colorPalette.monotone[0],
})

const PanelBody = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
})

const ThumbnailScrollArea = styled('div')({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
})
